/**
 * v0.7 Phase 2: dashboard landing page — cross-VM fleet overview.
 *
 * Fetches /api/vm/summary from every VM in the registry in parallel and
 * renders a card per VM + a cross-VM alerts panel at the top. Click a VM
 * card → navigate to /vm/<vm_id> which renders the existing single-VM
 * Dashboard scoped to that backend.
 *
 * VM list comes from /api/backends (same source as the top-bar switcher).
 * Backends with empty or path-only URLs (demo, staging-cloudrun) are skipped.
 */

import { useEffect, useState } from "react";

import type { VmSummary } from "./types";
import { Panel } from "./components";

interface VmEntry {
  id: string;
  fqdn: string;
  label: string;
}

interface LandingProps {
  onNavigateToVm: (vmId: string) => void;
  onNavigateToFleetGit?: () => void;
  defaultBaseUrl: string;
  authToken: string | null;
}

type FetchState = { kind: "loading" } | { kind: "ok"; summary: VmSummary } | { kind: "error"; message: string };

interface FleetVm {
  id: string;
  label: string;
  url: string;
  summary: VmSummary | null;
  error: string | null;
}

export function Landing({ onNavigateToVm, onNavigateToFleetGit, defaultBaseUrl, authToken }: LandingProps) {
  const [vms, setVms] = useState<VmEntry[]>([]);
  const [vmStates, setVmStates] = useState<Record<string, FetchState>>({});

  // Single call to /api/fleet/summary — the backend fans out to each VM
  // server-side, avoiding browser mixed-content (HTTPS dashboard → HTTP VMs)
  // and CORS failures. Polls every 10s.
  useEffect(() => {
    let cancelled = false;

    async function fetchFleet() {
      try {
        const res = await fetch(`${defaultBaseUrl}/api/fleet/summary`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { vms: FleetVm[] };
        if (cancelled) return;

        setVms(data.vms.map((v) => ({ id: v.id, fqdn: v.url, label: v.label })));
        setVmStates(
          Object.fromEntries(
            data.vms.map((v) => [
              v.id,
              v.summary
                ? ({ kind: "ok", summary: v.summary } as FetchState)
                : ({ kind: "error", message: v.error ?? "unreachable" } as FetchState),
            ]),
          ),
        );
      } catch (e) {
        if (cancelled) return;
        const fallback: VmEntry[] = [{ id: "vm-0", fqdn: defaultBaseUrl, label: "Fleet (fallback)" }];
        setVms(fallback);
        setVmStates({ "vm-0": { kind: "error", message: e instanceof Error ? e.message : String(e) } });
      }
    }

    void fetchFleet();
    const t = setInterval(() => void fetchFleet(), 10_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [defaultBaseUrl, authToken]);

  // Aggregate alerts across all VMs for the top banner
  const allAlerts = Object.entries(vmStates)
    .flatMap(([vmId, state]) => (state.kind === "ok" ? state.summary.alerts.map((a) => ({ vmId, ...a })) : []))
    .sort((a, b) => (a.severity === "crit" ? -1 : b.severity === "crit" ? 1 : a.severity === "warn" ? -1 : 1));

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 12 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Orchestrator — Fleet Overview</h1>
        {onNavigateToFleetGit && (
          <button
            className="btn btn-sm"
            onClick={onNavigateToFleetGit}
            title="Fleet-wide git-health: dirty/behind/diverged worktrees + cron liveness"
          >
            Fleet Git-Health →
          </button>
        )}
      </div>

      {allAlerts.length > 0 && (
        <Panel title={`Cross-VM alerts (${allAlerts.length})`} flush>
          <div style={{ padding: "8px 12px" }}>
            {allAlerts.map((a, i) => (
              <div
                key={i}
                style={{
                  padding: "4px 0",
                  borderBottom: i < allAlerts.length - 1 ? "1px solid var(--bg-2)" : "none",
                  fontSize: 12.5,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <span
                  className="mono"
                  style={{
                    color:
                      a.severity === "crit"
                        ? "var(--status-stale)"
                        : a.severity === "warn"
                          ? "var(--status-blocked)"
                          : "var(--fg-2)",
                    minWidth: 50,
                  }}
                >
                  {a.severity.toUpperCase()}
                </span>
                <span className="mono" style={{ color: "var(--fg-2)", minWidth: 60 }}>
                  {a.vmId}
                </span>
                <span style={{ color: "var(--fg-1)" }}>{a.detail}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        {vms.map((vm) => (
          <VmCard
            key={vm.id}
            vm={vm}
            state={vmStates[vm.id] ?? { kind: "loading" }}
            onClick={() => onNavigateToVm(vm.id)}
          />
        ))}
      </div>
    </div>
  );
}

function VmCard({ vm, state, onClick }: { vm: VmEntry; state: FetchState; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--bg-1)",
        border: "1px solid var(--bg-2)",
        borderRadius: 6,
        padding: 14,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = "var(--accent)")}
      onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.borderColor = "var(--bg-2)")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong style={{ fontSize: 14 }}>{state.kind === "ok" ? state.summary.label || vm.label : vm.label}</strong>
        <span className="mono" style={{ fontSize: 10.5, color: "var(--fg-2)" }}>
          {vm.id}
        </span>
      </div>

      {state.kind === "loading" && <div style={{ color: "var(--fg-2)", fontSize: 12 }}>Loading…</div>}

      {state.kind === "error" && (
        <div style={{ color: "var(--status-stale)", fontSize: 12 }}>
          Unreachable: <span className="mono">{state.message}</span>
        </div>
      )}

      {state.kind === "ok" && (
        <>
          <div style={{ fontSize: 12, color: "var(--fg-2)" }}>
            Slots:{" "}
            <span className="mono" style={{ color: "var(--fg-1)" }}>
              {state.summary.slots_total}
            </span>{" "}
            (<span style={{ color: "var(--status-working)" }}>{state.summary.slots_working} working</span>
            {state.summary.slots_idle > 0 && (
              <>
                , <span style={{ color: "var(--fg-2)" }}>{state.summary.slots_idle} idle</span>
              </>
            )}
            {state.summary.slots_stale > 0 && (
              <>
                , <span style={{ color: "var(--status-stale)" }}>{state.summary.slots_stale} stale</span>
              </>
            )}
            {state.summary.slots_paused > 0 && (
              <>
                , <span style={{ color: "var(--fg-2)" }}>{state.summary.slots_paused} paused</span>
              </>
            )}
            )
          </div>
          <div style={{ fontSize: 12, color: "var(--fg-2)" }}>
            Backlog:{" "}
            <span className="mono" style={{ color: "var(--fg-1)" }}>
              {state.summary.backlog_total}
            </span>{" "}
            ({state.summary.backlog_done} done, {state.summary.backlog_queued} queued,{" "}
            {state.summary.backlog_dispatched} dispatched)
          </div>
          {state.summary.watchdog && state.summary.watchdog.enabled && (
            <div style={{ fontSize: 12, color: "var(--fg-2)" }}>
              Watchdog:{" "}
              <span
                className="mono"
                style={{
                  color:
                    state.summary.watchdog.kills_today >= state.summary.watchdog.daily_cap
                      ? "var(--status-stale)"
                      : state.summary.watchdog.kills_today > 0
                        ? "var(--status-blocked)"
                        : "var(--fg-1)",
                }}
              >
                {state.summary.watchdog.kills_today}/{state.summary.watchdog.daily_cap} kills
              </span>{" "}
              today
              {state.summary.watchdog.dormant && (
                <span style={{ color: "var(--status-stale)" }}> · DORMANT (cap hit)</span>
              )}
              {!state.summary.watchdog.dormant && state.summary.watchdog.flapping && (
                <span style={{ color: "var(--status-blocked)" }}>
                  {" "}
                  · FLAPPING ({state.summary.watchdog.slots_killed_last_hour} slots/hr)
                </span>
              )}
            </div>
          )}
          {state.summary.primary_account_id && (
            <div style={{ fontSize: 12, color: "var(--fg-2)" }}>
              Primary: <span className="mono">{state.summary.primary_account_id}</span>
              {state.summary.primary_account_pct !== null && (
                <>
                  {" "}
                  at{" "}
                  <span
                    style={{
                      color:
                        (state.summary.primary_account_pct ?? 0) >= 80
                          ? "var(--status-stale)"
                          : (state.summary.primary_account_pct ?? 0) >= 60
                            ? "var(--status-blocked)"
                            : "var(--fg-1)",
                    }}
                  >
                    {state.summary.primary_account_pct}%
                  </span>
                </>
              )}
            </div>
          )}
          {state.summary.master_plans.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--fg-2)" }}>
              {state.summary.master_plans.length} master plan{state.summary.master_plans.length === 1 ? "" : "s"}:{" "}
              {state.summary.master_plans
                .map((p) =>
                  p
                    .split("/")
                    .pop()
                    ?.replace(/_\d{4}_\d{2}_\d{2}\.md$/, ""),
                )
                .filter(Boolean)
                .join(", ")}
            </div>
          )}
          {state.summary.alerts.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--status-blocked)" }}>
              ⚠ {state.summary.alerts.length} alert{state.summary.alerts.length === 1 ? "" : "s"}
            </div>
          )}
        </>
      )}
    </div>
  );
}
